/**
 * Meta Pixel (Facebook Pixel) tracking utility
 * Provides helper functions to track events with the Meta Pixel
 */

/**
 * Check if the Meta Pixel is available and initialized
 */
export const isMetaPixelAvailable = (): boolean => {
  return typeof window !== 'undefined' && window.fbq !== undefined
}

/**
 * Track a standard Meta Pixel event
 * @param eventName - The name of the standard event (e.g., 'Purchase', 'AddToCart', 'PageView')
 * @param params - Optional event parameters
 * @param userData - Optional hashed user data for improved attribution (em, external_id, etc.)
 */
export const trackMetaPixelEvent = (
  eventName: string,
  params?: Record<string, any>,
  userData?: Record<string, string>
): void => {
  if (!isMetaPixelAvailable()) {
    console.warn('Meta Pixel is not available')
    return
  }

  try {
    if (userData && Object.keys(userData).length > 0) {
      window.fbq('track', eventName, params, { userData })
    } else {
      window.fbq('track', eventName, params)
    }
    console.log(`[Meta Pixel] Tracked event: ${eventName}`, params || {})
  } catch (error) {
    console.error(`[Meta Pixel] Error tracking event ${eventName}:`, error)
  }
}

/**
 * Track a custom Meta Pixel event
 * @param eventName - The name of your custom event (max 50 characters)
 * @param params - Optional parameters object with event data
 */
export const trackMetaPixelCustomEvent = (
  eventName: string,
  params?: Record<string, any>
): void => {
  if (!isMetaPixelAvailable()) {
    console.warn('Meta Pixel is not available')
    return
  }

  if (eventName.length > 50) {
    console.warn(`[Meta Pixel] Custom event name exceeds 50 characters: "${eventName}"`)
    return
  }

  try {
    window.fbq('trackCustom', eventName, {
      ...params,
      AdvertiserTrackingEnabled: true,
    })
    console.log(`[Meta Pixel] Tracked custom event: ${eventName}`, params || {})
  } catch (error) {
    console.error(`[Meta Pixel] Error tracking custom event ${eventName}:`, error)
  }
}

/**
 * Track when a user creates their first collection
 * Uses a custom event for better tracking specificity
 * @param collectionTitle - Optional: the title of the collection created
 */
export const trackFirstCollectionCreated = (
  collectionTitle?: string
): void => {
  console.log('[Meta Pixel] Tracking first collection creation:', {
    collection_name: collectionTitle || 'Untitled Collection',
    timestamp: new Date().toISOString()
  })

  trackMetaPixelCustomEvent('FirstCreateCollection', {
    collection_name: collectionTitle || 'Untitled Collection',
    event_type: 'first_collection_creation',
    AdvertiserTrackingEnabled: true,
  })

  console.log('[Meta Pixel] First collection creation event sent successfully')
}

/**
 * Track when a user creates a collection (subsequent collections)
 * Uses a custom event for better tracking specificity
 * @param collectionTitle - Optional: the title of the collection created
 */
export const trackCollectionCreated = (
  collectionTitle?: string
): void => {
  console.log('[Meta Pixel] Tracking collection creation:', {
    collection_name: collectionTitle || 'Untitled Collection',
    timestamp: new Date().toISOString()
  })

  trackMetaPixelCustomEvent('CreateCollection', {
    collection_name: collectionTitle || 'Untitled Collection',
    event_type: 'collection_creation',
    AdvertiserTrackingEnabled: true,
  })

  console.log('[Meta Pixel] Collection creation event sent successfully')
}

/**
 * Track when a user signs up
 * Uses a custom event for better tracking specificity
 * @param authMethod - The authentication method used (e.g., 'email', 'google', 'apple')
 * @param hasInstagram - Whether the user provided an Instagram handle
 */
export const trackSignUp = (
  email: string,
  authMethod: string = 'email',
  hasInstagram: boolean = false
): void => {
  console.log('[Meta Pixel] Tracking user sign up:', {
    email: email,
    auth_method: authMethod,
    has_instagram: hasInstagram,
    timestamp: new Date().toISOString()
  })

  trackMetaPixelCustomEvent('SignUp', {
    email: email,
    auth_method: authMethod,
    has_instagram: hasInstagram,
    event_type: 'user_signup',
    AdvertiserTrackingEnabled: true,
  })

  console.log('[Meta Pixel] Sign up event sent successfully')
}

/**
 * Track when a user follows an artist for the first time
 * Uses a custom event for better tracking specificity
 * @param artistId - The ID of the artist being followed
 */
export const trackFirstFollowArtist = (
  artistId: string
): void => {
  console.log('[Meta Pixel] Tracking first artist follow:', {
    artist_id: artistId,
    timestamp: new Date().toISOString()
  })

  trackMetaPixelCustomEvent('FirstFollowArtist', {
    artist_id: artistId,
    event_type: 'first_artist_follow',
    AdvertiserTrackingEnabled: true,
  })

  console.log('[Meta Pixel] First artist follow event sent successfully')
}

/**
 * Track when a user follows an artist (subsequent follows)
 * Uses a custom event for better tracking specificity
 * @param artistId - The ID of the artist being followed
 */
export const trackFollowArtist = (
  artistId: string
): void => {
  console.log('[Meta Pixel] Tracking artist follow:', {
    artist_id: artistId,
    timestamp: new Date().toISOString()
  })

  trackMetaPixelCustomEvent('FollowArtist', {
    artist_id: artistId,
    event_type: 'artist_follow',
    AdvertiserTrackingEnabled: true,
  })

  console.log('[Meta Pixel] Artist follow event sent successfully')
}

